import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  createSupportTicket,
  fetchSupportAssignees,
  fetchSupportRequesters,
  uploadSupportAttachment,
} from '../api/authApi';

const TICKET_STATUSES = [
  'open',
  'waiting_for_support',
  'waiting_for_customer',
  'resolved',
  'closed',
];

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const MAX_ATTACHMENT_MB = 10;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'txt', 'csv', 'xlsx', 'docx', 'zip'];

const emptyForm = {
  customer_id: '',
  tenant_id: '',
  requested_by_user_id: '',
  theme: '',
  description: '',
  priority: 'normal',
  status: 'open',
  mask_number: '',
  type: '',
  deadline: '',
  awaiting_user_id: '',
};

function extractApiError(err, fallback) {
  const data = err?.response?.data;
  if (data?.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors).flat().find(Boolean);
    if (first) return String(first);
  }
  if (data?.message) return String(data.message);
  return fallback;
}

function formatBytes(size) {
  if (!Number.isFinite(size)) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtension(name) {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

function validateLocalFile(file, t) {
  const maxBytes = MAX_ATTACHMENT_MB * 1024 * 1024;
  if (file.size > maxBytes) {
    return t('support:attachment_too_large', { max: MAX_ATTACHMENT_MB, name: file.name });
  }
  const ext = fileExtension(file.name);
  if (ext && !ALLOWED_EXTENSIONS.includes(ext)) {
    return t('support:attachment_type_denied', { name: file.name });
  }
  return null;
}

export function CreateSupportTicketDialog({ open, onClose, onCreated, customers = [], tenants = [] }) {
  const { t } = useTranslation(['support', 'common', 'status']);
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState('form'); // form | partial
  const [createdTicket, setCreatedTicket] = useState(null);
  const [files, setFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loadingRequesters, setLoadingRequesters] = useState(false);
  const submitLockRef = useRef(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setForm(emptyForm);
    setFieldErrors({});
    setError(null);
    setSubmitting(false);
    setPhase('form');
    setCreatedTicket(null);
    setFiles([]);
    setFailedFiles([]);
    setRequesters([]);
    submitLockRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    let cancelled = false;
    fetchSupportAssignees()
      .then((list) => {
        if (!cancelled) setAssignees(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAssignees([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !form.customer_id) {
      setRequesters([]);
      return;
    }

    let cancelled = false;
    setLoadingRequesters(true);
    fetchSupportRequesters(Number(form.customer_id))
      .then((list) => {
        if (!cancelled) setRequesters(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setRequesters([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRequesters(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, form.customer_id]);

  const filteredTenants = form.customer_id
    ? tenants.filter((tenant) => String(tenant.customer_id) === String(form.customer_id))
    : [];

  // Operator create requires customer → tenant context before on-behalf requester.
  const requesterEnabled = Boolean(form.customer_id && form.tenant_id) && !loadingRequesters && !submitting;
  const canSubmit = Boolean(
    form.customer_id
    && form.tenant_id
    && form.theme.trim()
    && form.description.trim()
    && !submitting
    && phase === 'form'
  );

  const handleCustomerChange = (event) => {
    const customerId = event.target.value;
    setForm((prev) => ({
      ...prev,
      customer_id: customerId,
      tenant_id: '',
      requested_by_user_id: '',
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.customer_id;
      delete next.tenant_id;
      delete next.requested_by_user_id;
      return next;
    });
  };

  const handleTenantChange = (event) => {
    setForm((prev) => ({
      ...prev,
      tenant_id: event.target.value,
      requested_by_user_id: '',
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.tenant_id;
      delete next.requested_by_user_id;
      return next;
    });
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;

    const nextErrors = [];
    const accepted = [];
    selected.forEach((file) => {
      const localError = validateLocalFile(file, t);
      if (localError) {
        nextErrors.push(localError);
      } else {
        accepted.push(file);
      }
    });

    if (nextErrors.length) {
      setError(nextErrors[0]);
    }
    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFileAt = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFilesToTicket = async (ticket, fileList) => {
    const failed = [];
    for (const file of fileList) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await uploadSupportAttachment(ticket.id, formData);
      } catch {
        failed.push(file);
      }
    }
    return failed;
  };

  const finishSuccess = (ticket) => {
    const snapshot = ticket;
    resetState();
    onCreated?.(snapshot);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (submitLockRef.current || submitting) return;

    const nextFieldErrors = {};
    if (!form.customer_id) nextFieldErrors.customer_id = t('support:customer_required');
    if (!form.tenant_id) nextFieldErrors.tenant_id = t('support:tenant_required');
    if (!form.theme.trim()) nextFieldErrors.theme = t('support:theme_required');
    if (!form.description.trim()) nextFieldErrors.description = t('support:description_required');

    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      setError(t('support:create_validation_failed'));
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    const payload = {
      theme: form.theme.trim(),
      description: form.description.trim(),
      priority: form.priority,
      status: form.status,
      mask_number: form.mask_number.trim() || undefined,
      type: form.type.trim() || undefined,
      deadline: form.deadline || undefined,
      customer_id: Number(form.customer_id),
      tenant_id: Number(form.tenant_id),
    };

    if (form.requested_by_user_id) {
      payload.requested_by_user_id = Number(form.requested_by_user_id);
      const requester = requesters.find((u) => String(u.id) === String(form.requested_by_user_id));
      if (requester) {
        payload.requested_by_name = requester.name;
        payload.requested_by_email = requester.email;
      }
    }
    if (form.awaiting_user_id) {
      payload.awaiting_user_id = Number(form.awaiting_user_id);
    }

    // Never send client ticket identity / prefix / source.
    delete payload.public_number;
    delete payload.defined_id;
    delete payload.support_ticket_prefix;
    delete payload.source;

    try {
      const ticket = await createSupportTicket(payload);
      setCreatedTicket(ticket);

      if (files.length) {
        const failed = await uploadFilesToTicket(ticket, files);
        if (failed.length) {
          setFailedFiles(failed);
          setPhase('partial');
          setError(
            t('support:create_partial_attachments', {
              number: ticket.public_number || ticket.id,
            })
          );
          submitLockRef.current = false;
          return;
        }
      }

      finishSuccess(ticket);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, messages]) => {
          if (Array.isArray(messages) && messages[0]) mapped[key] = String(messages[0]);
        });
        if (mapped.theme || mapped.subject) mapped.theme = mapped.theme || mapped.subject;
        setFieldErrors(mapped);
      }
      setError(extractApiError(err, t('support:create_failed')));
      submitLockRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetryFailedUploads = async () => {
    if (!createdTicket || submitLockRef.current || submitting) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const failed = await uploadFilesToTicket(createdTicket, failedFiles);
      if (failed.length) {
        setFailedFiles(failed);
        setError(
          t('support:create_partial_attachments', {
            number: createdTicket.public_number || createdTicket.id,
          })
        );
        return;
      }
      finishSuccess(createdTicket);
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const handleContinueAfterPartial = () => {
    if (!createdTicket) return;
    finishSuccess(createdTicket);
  };

  const handleClose = () => {
    if (submitting) return;
    if (phase === 'partial' && createdTicket) {
      finishSuccess(createdTicket);
      return;
    }
    resetState();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="create-support-ticket-title"
    >
      <DialogTitle id="create-support-ticket-title">{t('support:new_ticket')}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && (
          <Alert severity={phase === 'partial' ? 'warning' : 'error'}>{error}</Alert>
        )}

        {phase === 'partial' && createdTicket ? (
          <Stack spacing={2}>
            <Alert severity="success">
              {t('support:ticket_created_number', {
                number: createdTicket.public_number || createdTicket.id,
              })}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {t('support:attachments_partial_help')}
            </Typography>
            <List dense>
              {failedFiles.map((file, index) => (
                <ListItem key={`${file.name}-${index}`} disableGutters>
                  <ListItemText
                    primary={file.name}
                    secondary={`${formatBytes(file.size)} · ${t('support:upload_failed')}`}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        ) : (
          <>
            <Typography variant="subtitle2" color="text.secondary">
              {t('support:create_section_context')}
            </Typography>

            <FormControl fullWidth size="small" required error={Boolean(fieldErrors.customer_id)}>
              <InputLabel id="create-ticket-customer-label">{t('common:customer')}</InputLabel>
              <Select
                labelId="create-ticket-customer-label"
                id="create-ticket-customer"
                label={t('common:customer')}
                value={form.customer_id}
                onChange={handleCustomerChange}
                disabled={submitting}
              >
                <MenuItem value="">—</MenuItem>
                {customers.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.customer_id ? <FormHelperText>{fieldErrors.customer_id}</FormHelperText> : null}
            </FormControl>

            <FormControl fullWidth size="small" required error={Boolean(fieldErrors.tenant_id)}>
              <InputLabel id="create-ticket-tenant-label">{t('common:tenant')}</InputLabel>
              <Select
                labelId="create-ticket-tenant-label"
                id="create-ticket-tenant"
                label={t('common:tenant')}
                value={form.tenant_id}
                onChange={handleTenantChange}
                disabled={submitting || !form.customer_id}
              >
                <MenuItem value="">—</MenuItem>
                {filteredTenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.tenant_id ? <FormHelperText>{fieldErrors.tenant_id}</FormHelperText> : null}
            </FormControl>

            <FormControl fullWidth size="small" error={Boolean(fieldErrors.requested_by_user_id)}>
              <InputLabel id="create-ticket-requester-label">{t('support:requester')}</InputLabel>
              <Select
                labelId="create-ticket-requester-label"
                id="create-ticket-requester"
                label={t('support:requester')}
                value={form.requested_by_user_id}
                onChange={handleChange('requested_by_user_id')}
                disabled={!requesterEnabled}
              >
                <MenuItem value="">{t('support:requester_none')}</MenuItem>
                {requesters.map((user) => (
                  <MenuItem key={user.id} value={String(user.id)}>
                    {user.name}
                    {user.email ? ` (${user.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {fieldErrors.requested_by_user_id
                  || (!form.customer_id || !form.tenant_id
                    ? t('support:requester_needs_context')
                    : t('support:requester_help'))}
              </FormHelperText>
            </FormControl>

            <Typography variant="subtitle2" color="text.secondary">
              {t('support:create_section_ticket')}
            </Typography>

            <TextField
              label={t('support:theme_subject')}
              required
              fullWidth
              size="small"
              value={form.theme}
              onChange={handleChange('theme')}
              error={Boolean(fieldErrors.theme || fieldErrors.subject)}
              helperText={fieldErrors.theme || fieldErrors.subject || ''}
              disabled={submitting}
            />

            <TextField
              label={t('common:description')}
              required
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.description}
              onChange={handleChange('description')}
              error={Boolean(fieldErrors.description)}
              helperText={fieldErrors.description || ''}
              disabled={submitting}
            />

            <Typography variant="subtitle2" color="text.secondary">
              {t('support:create_section_workflow')}
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>{t('support:tickets.priority')}</InputLabel>
              <Select
                label={t('support:tickets.priority')}
                value={form.priority}
                onChange={handleChange('priority')}
                disabled={submitting}
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {t(`status:support_priority.${p}`, p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>{t('common:status')}</InputLabel>
              <Select
                label={t('common:status')}
                value={form.status}
                onChange={handleChange('status')}
                disabled={submitting}
              >
                {TICKET_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`status:support.${s}`, s)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('support:status_on_create_help')}</FormHelperText>
            </FormControl>

            <TextField
              label={t('support:mask_number')}
              fullWidth
              size="small"
              value={form.mask_number}
              onChange={handleChange('mask_number')}
              disabled={submitting}
            />

            <TextField
              label={t('common:type')}
              fullWidth
              size="small"
              value={form.type}
              onChange={handleChange('type')}
              disabled={submitting}
            />

            <TextField
              label={t('support:deadline')}
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.deadline}
              onChange={handleChange('deadline')}
              disabled={submitting}
            />

            <FormControl fullWidth size="small">
              <InputLabel>{t('support:awaiting_user')}</InputLabel>
              <Select
                label={t('support:awaiting_user')}
                value={form.awaiting_user_id}
                onChange={handleChange('awaiting_user_id')}
                disabled={submitting}
              >
                <MenuItem value="">—</MenuItem>
                {assignees.map((user) => (
                  <MenuItem key={user.id} value={String(user.id)}>
                    {user.name}
                    {user.email ? ` (${user.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" color="text.secondary">
              {t('support:create_section_attachments')}
            </Typography>

            <Box>
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={submitting}
              >
                {t('support:add_attachments')}
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  multiple
                  onChange={handleFilesSelected}
                />
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('support:attachments_limits', { max: MAX_ATTACHMENT_MB })}
              </Typography>
            </Box>

            {files.length > 0 ? (
              <List dense disablePadding>
                {files.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${file.size}-${index}`}
                    disableGutters
                    secondaryAction={
                      <Button size="small" onClick={() => removeFileAt(index)} disabled={submitting}>
                        {t('support:remove_attachment')}
                      </Button>
                    }
                  >
                    <ListItemText primary={file.name} secondary={formatBytes(file.size)} />
                  </ListItem>
                ))}
              </List>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {phase === 'partial' ? (
          <>
            <Button onClick={handleContinueAfterPartial} disabled={submitting}>
              {t('support:continue_to_ticket')}
            </Button>
            <Button variant="contained" onClick={handleRetryFailedUploads} disabled={submitting}>
              {submitting ? t('support:uploading') : t('support:retry_failed_uploads')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              {t('common:cancel')}
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? t('support:creating') : t('support:create_ticket')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
